import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Trash2, Undo2, Clock } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { ConfirmDialog } from '../components/ui/Modal';
import { DataGrid, Column } from '../components/advanced/DataGrid';
import { PageLayout } from '@/components/layout/PageLayout';
import { tabRecyclingService } from '../services/tabRecycling';
import { openBrowserTabs } from '@/services/extensionBridge';
import { toast } from 'sonner';

interface RecycledTabRow {
  id: string;
  title: string;
  url: string;
  closedAt: string;
  reason: string;
  canRestore: boolean;
}

function formatClosedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function TabRecyclingCenter() {
  const [recycled, setRecycled] = useState<RecycledTabRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEmptyBinConfirm, setShowEmptyBinConfirm] = useState(false);
  const [selectedTabId, setSelectedTabId] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const fetchRecycledTabs = useCallback(async () => {
    setIsLoading(true);
    try {
      const tabs = await tabRecyclingService.listRecycledTabs();
      const rows: RecycledTabRow[] = tabs.map((t) => ({
        id: t.id,
        title: t.title?.trim() ? t.title : 'Untitled',
        url: t.url ?? '',
        closedAt: formatClosedAt(t.closed_at),
        reason: 'Closed tab',
        canRestore: !t.expires_at || new Date(t.expires_at).getTime() > Date.now(),
      }));
      setRecycled(rows);
    } catch (error) {
      console.error('Failed to fetch recycled tabs:', error);
      toast.error('Failed to load recycled tabs');
      setRecycled([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecycledTabs();
  }, [fetchRecycledTabs]);

  useEffect(() => {
    const refreshIfVisible = () => {
      if (document.visibilityState === 'visible') {
        void fetchRecycledTabs();
      }
    };
    document.addEventListener('visibilitychange', refreshIfVisible);
    return () => document.removeEventListener('visibilitychange', refreshIfVisible);
  }, [fetchRecycledTabs]);

  const handleRestore = async (tabId: string) => {
    try {
      const restored = await tabRecyclingService.restoreRecycledTab(tabId);
      if (restored.url) {
        openBrowserTabs([restored.url]);
      }
      toast.success(restored.title ? `Restored: ${restored.title}` : 'Tab restored');
      await fetchRecycledTabs();
    } catch (error) {
      console.error(error);
      toast.error('Failed to restore tab');
    }
  };

  const handleDelete = (tabId: string) => {
    setSelectedTabId(tabId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteTab = async () => {
    if (!selectedTabId) return;
    setActionBusy(true);
    try {
      await tabRecyclingService.permanentlyDeleteRecycledTab(selectedTabId);
      toast.success('Tab permanently deleted');
      setShowDeleteConfirm(false);
      setSelectedTabId(null);
      await fetchRecycledTabs();
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete tab');
    } finally {
      setActionBusy(false);
    }
  };

  const handleRestoreAll = async () => {
    if (recycled.length === 0) return;
    setActionBusy(true);
    try {
      const results = await Promise.allSettled(
        recycled.filter((r) => r.canRestore).map(async (r) => {
          const restored = await tabRecyclingService.restoreRecycledTab(r.id);
          if (restored.url) {
            openBrowserTabs([restored.url], false);
          }
          return restored;
        }),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      const ok = results.length - failed;
      if (failed === 0) toast.success('All tabs restored');
      else if (ok > 0) toast.warning(`${ok} restored, ${failed} failed`);
      else toast.error('Failed to restore tabs');
      await fetchRecycledTabs();
    } catch (error) {
      console.error(error);
      toast.error('Failed to restore all tabs');
    } finally {
      setActionBusy(false);
    }
  };

  const handleEmptyBin = () => {
    setShowEmptyBinConfirm(true);
  };

  const confirmEmptyBin = async () => {
    setActionBusy(true);
    try {
      await tabRecyclingService.emptyRecyclingBin();
      toast.success('Recycle bin emptied');
      setShowEmptyBinConfirm(false);
      await fetchRecycledTabs();
    } catch (error) {
      console.error(error);
      toast.error('Failed to empty recycle bin');
    } finally {
      setActionBusy(false);
    }
  };

  const restorableCount = recycled.filter((r) => r.canRestore).length;

  const columns: Column<RecycledTabRow>[] = [
    {
      key: 'title',
      header: 'Tab',
      sortable: true,
      render: (value, row) => (
        <div>
          <p className="font-medium text-khiimori-text-primary">{value}</p>
          <p className="text-xs text-khiimori-text-secondary truncate">{row.url}</p>
        </div>
      ),
    },
    { key: 'reason', header: 'Reason', sortable: true },
    {
      key: 'closedAt',
      header: 'Closed',
      sortable: true,
      render: (value) => (
        <span className="flex items-center gap-1 text-sm text-khiimori-text-muted">
          <Clock className="w-4 h-4" />
          {value}
        </span>
      ),
    },
    {
      key: 'canRestore',
      header: 'Actions',
      render: (value, row) => (
        <div className="flex gap-2">
          {value && (
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Undo2 className="w-4 h-4" />}
              onClick={() => handleRestore(row.id)}
              disabled={actionBusy}
            >
              Restore
            </Button>
          )}
          <Button
            variant="danger"
            size="sm"
            leftIcon={<Trash2 className="w-4 h-4" />}
            onClick={() => handleDelete(row.id)}
            disabled={actionBusy}
            aria-label="Delete tab"
          />
        </div>
      ),
    },
  ];

  return (
    <PageLayout
      title="Tab Recycling Center"
      description="Recover closed tabs and manage tab lifecycle"
    >
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        {[
          { label: 'Recycled', value: recycled.length, icon: <RefreshCw className="w-8 h-8 text-success-400" /> },
          { label: 'Restorable', value: restorableCount, icon: <Undo2 className="w-8 h-8 text-info-400" /> },
          { label: 'Max bin size', value: 100, icon: <Trash2 className="w-8 h-8 text-warning-400" /> },
          { label: 'Retention', value: '7 days', icon: <Clock className="w-8 h-8 text-primary-400" /> },
        ].map((stat, idx) => (
          <Card key={idx} className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-khiimori-text-muted">{stat.label}</p>
                <p className="text-3xl font-bold text-khiimori-text-primary">{stat.value}</p>
              </div>
              {stat.icon}
            </div>
          </Card>
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
        </div>
      ) : (
        <DataGrid data={recycled} columns={columns} searchable />
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-6">
          <h3 className="font-semibold text-khiimori-text-primary mb-3">Bin policy</h3>
          <div className="space-y-2 text-sm">
            <p className="flex justify-between">
              <span className="text-khiimori-text-muted">Retention</span>
              <span className="font-medium">7 days</span>
            </p>
            <p className="flex justify-between">
              <span className="text-khiimori-text-muted">Max items</span>
              <span className="font-medium">100</span>
            </p>
          </div>
        </Card>
        <Card className="p-6">
          <Button
            variant="primary"
            onClick={handleRestoreAll}
            disabled={actionBusy || restorableCount === 0}
            className="w-full"
          >
            Restore All
          </Button>
        </Card>
        <Card className="p-6">
          <Button
            variant="danger"
            onClick={handleEmptyBin}
            disabled={actionBusy || recycled.length === 0}
            className="w-full"
          >
            Empty Recycle Bin
          </Button>
        </Card>
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => !actionBusy && setShowDeleteConfirm(false)}
        onConfirm={confirmDeleteTab}
        title="Delete Tab"
        message="Are you sure you want to permanently delete this tab? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={actionBusy}
      />

      <ConfirmDialog
        isOpen={showEmptyBinConfirm}
        onClose={() => !actionBusy && setShowEmptyBinConfirm(false)}
        onConfirm={confirmEmptyBin}
        title="Empty Recycle Bin"
        message="Are you sure you want to permanently delete all items in the recycle bin? This action cannot be undone."
        confirmText="Empty Bin"
        cancelText="Cancel"
        variant="danger"
        isLoading={actionBusy}
      />
    </PageLayout>
  );
}
